import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams, Link } from "react-router-dom";
import { Eye, Link as LinkIcon } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import type { Complaint, ComplaintStatus } from "@/lib/types";
import { timeAgo, fullDate } from "@/lib/timeAgo";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { EmptyState } from "@/components/shared/EmptyState";
import { ComplaintChip, PlatformChip, SeverityChip } from "@/components/shared/Chips";
import { PagedToolbar, Pagination } from "@/components/shared/Pagination";
import { Customer360Sheet } from "@/components/shared/Customer360Sheet";
import { CaptureLinkDialog } from "@/components/shared/CaptureLinkDialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const STATUSES: ComplaintStatus[] = ["open", "in_review", "resolved", "ignored"];

export const Complaints: React.FC = () => {
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [customer360Handle, setCustomer360Handle] = useState<string | null>(null);
  const [captureLinkFor, setCaptureLinkFor] = useState<Complaint | null>(null);
  const platformFilter = params.get("platform") || "all";
  const statusFilter = params.get("status") || "all";
  const severityFilter = params.get("severity") || "all";
  const search = params.get("q") || "";
  const authorFilter = params.get("author_handle") || "";
  const page = Math.max(1, parseInt(params.get("page") || "1", 10) || 1);
  const pageSize = Math.max(10, Math.min(500, parseInt(params.get("page_size") || "50", 10) || 50));

  const complaints = useQuery<{ items: Complaint[]; total: number; offset: number; limit: number }>({
    queryKey: ["complaints", platformFilter, statusFilter, severityFilter, search, authorFilter, page, pageSize],
    queryFn: async () => {
      const q = new URLSearchParams();
      if (platformFilter !== "all") q.set("platform", platformFilter);
      if (statusFilter !== "all") q.set("status", statusFilter);
      if (severityFilter !== "all") q.set("severity", severityFilter);
      if (search.trim()) q.set("q", search.trim());
      if (authorFilter.trim()) q.set("author_handle", authorFilter.trim());
      q.set("offset", String((page - 1) * pageSize));
      q.set("limit", String(pageSize));
      return (await api.get(`/complaints?${q.toString()}`)).data;
    },
  });

  const items = complaints.data?.items || [];
  const total = complaints.data?.total || 0;

  const update = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: ComplaintStatus }) =>
      (await api.patch(`/complaints/${id}`, { status })).data,
    onSuccess: () => {
      toast.success("Complaint updated");
      qc.invalidateQueries({ queryKey: ["complaints"] });
    },
  });

  const setParam = (k: string, v: string | null) => {
    const p = new URLSearchParams(params);
    if (v === null || v === "") p.delete(k); else p.set(k, v);
    p.set("page", "1");
    setParams(p);
  };

  return (
    <DashboardLayout
      title="Complaints"
      subtitle="Every complaint captured, triaged and tracked."
      seoPath="/complaints"
      seoDescription="Complaints inbox — captured, triaged, and tracked to resolution across every connected platform."
    >
      <PagedToolbar
        testId="complaints-search"
        search={search}
        placeholder="Search complaints by text, author, or type…"
        onSearch={(v) => setParam("q", v)}
        authorFilter={authorFilter || undefined}
        onClearAuthor={() => setParam("author_handle", null)}
      />

      <div className="flex flex-wrap items-center gap-3 mb-5">
        <Select value={platformFilter} onValueChange={(v) => setParam("platform", v === "all" ? null : v)}>
          <SelectTrigger data-testid="filter-complaint-platform" className="w-44 bg-white">
            <SelectValue placeholder="Platform" />
          </SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">All platforms</SelectItem>
            <SelectItem value="youtube">YouTube</SelectItem>
            <SelectItem value="instagram">Instagram</SelectItem>
            <SelectItem value="facebook">Facebook</SelectItem>
            <SelectItem value="linkedin">LinkedIn</SelectItem>
            <SelectItem value="twitter">X / Twitter</SelectItem>
            <SelectItem value="tiktok">TikTok</SelectItem>
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={(v) => setParam("status", v === "all" ? null : v)}>
          <SelectTrigger data-testid="filter-complaint-status" className="w-44 bg-white">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">All statuses</SelectItem>
            {STATUSES.map((s) => (<SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>))}
          </SelectContent>
        </Select>
        <Select value={severityFilter} onValueChange={(v) => setParam("severity", v === "all" ? null : v)}>
          <SelectTrigger data-testid="filter-complaint-severity" className="w-44 bg-white">
            <SelectValue placeholder="Severity" />
          </SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">All severities</SelectItem>
            <SelectItem value="critical">Critical</SelectItem>
            <SelectItem value="high">High</SelectItem>
            <SelectItem value="medium">Medium</SelectItem>
            <SelectItem value="low">Low</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {complaints.isLoading ? (
        <div className="sg-skeleton h-64" />
      ) : items.length > 0 ? (
        <div className="sg-card overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="sg-label">Source</TableHead>
                <TableHead className="sg-label">Platform</TableHead>
                <TableHead className="sg-label">Severity</TableHead>
                <TableHead className="sg-label">Status</TableHead>
                <TableHead className="sg-label">Age</TableHead>
                <TableHead className="sg-label">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((c) => {
                const handle = (c.author_handle || "").replace(/^@/, "");
                return (
                  <TableRow key={c.complaint_id} data-testid={`complaint-row-${c.complaint_id}`}>
                    <TableCell className="max-w-md">
                      <button
                        type="button"
                        onClick={() => setCustomer360Handle(handle)}
                        data-testid={`complaint-author-${c.complaint_id}`}
                        className="text-sm font-medium hover:text-[color:var(--sg-primary)] hover:underline"
                        title={`Customer 360 for ${c.author_handle}`}
                      >
                        {c.author_handle}
                      </button>
                      <p className="text-xs text-[color:var(--sg-muted)] mt-0.5 line-clamp-2">
                        {c.source_text}
                      </p>
                    </TableCell>
                    <TableCell><PlatformChip platform={c.platform} /></TableCell>
                    <TableCell><SeverityChip severity={c.severity} /></TableCell>
                    <TableCell>
                      <Select
                        value={c.status}
                        onValueChange={(v) =>
                          update.mutate({ id: c.complaint_id, status: v as ComplaintStatus })
                        }
                      >
                        <SelectTrigger data-testid={`complaint-status-${c.complaint_id}`} className="w-36 bg-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-white">
                          {STATUSES.map((s) => (
                            <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <span title={fullDate(c.created_at)} className="text-xs text-[color:var(--sg-muted)]" data-testid={`complaint-date-${c.complaint_id}`}>
                        {timeAgo(c.created_at)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-3 flex-wrap">
                        <Link
                          to={`/comments?author_handle=${encodeURIComponent(handle)}`}
                          data-testid={`complaint-view-${c.complaint_id}`}
                          className="inline-flex items-center gap-1 text-xs text-[color:var(--sg-primary)] hover:underline font-medium"
                        >
                          <Eye className="h-3 w-3" /> Comments
                        </Link>
                        <button
                          type="button"
                          onClick={() => setCaptureLinkFor(c)}
                          data-testid={`complaint-capture-link-${c.complaint_id}`}
                          className="inline-flex items-center gap-1 text-xs text-[color:var(--sg-violet)] hover:underline font-medium"
                          title="Send capture link"
                        >
                          <LinkIcon className="h-3 w-3" /> Capture link
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      ) : (
        <EmptyState
          title={search || authorFilter ? "No complaints match your filters" : "No complaints"}
          description={search || authorFilter ? "Try clearing the search or the user filter." : "Nothing to triage right now — enjoy the calm."}
        />
      )}

      <Pagination
        testId="complaints-pagination"
        page={page}
        pageSize={pageSize}
        total={total}
        onPageChange={(p) => { const q = new URLSearchParams(params); q.set("page", String(p)); setParams(q); }}
        onPageSizeChange={(s) => { const q = new URLSearchParams(params); q.set("page_size", String(s)); q.set("page", "1"); setParams(q); }}
      />

      <Customer360Sheet
        open={!!customer360Handle}
        onOpenChange={(o) => !o && setCustomer360Handle(null)}
        handle={customer360Handle || ""}
        onFilterHere={
          customer360Handle
            ? () => setParam("author_handle", customer360Handle)
            : undefined
        }
      />

      <CaptureLinkDialog
        open={!!captureLinkFor}
        onOpenChange={(o) => !o && setCaptureLinkFor(null)}
        commentId={captureLinkFor?.comment_id}
      />
    </DashboardLayout>
  );
};
